import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Radio, Database, Clock, Tag as TagIcon, AlertTriangle } from 'lucide-react'
import { apiService } from '@/service/api'
import type { EventhouseConnectionSummary, EventhouseReadingRow } from '@/types/eventhouse'
import { parseTagInventory } from '@/lib/liveStreamDescription'
import { useTranslation } from '@/i18n'

/**
 * LiveStreamPreview — viewer body for a `live_stream` anchor document
 * (Eventhouse / streamed tags).
 *
 * Deliberately its OWN component rather than a branch inside
 * TimeseriesPreview: a live stream has no file, no SQLite db_path and no
 * copied values, so the file-shaped viewers don't describe it.
 *
 * Loads in two independent passes so the panel is useful immediately:
 *   1. metadata — GET v1/document/{id} (the harvest-written description) and
 *      GET v2/eventhouse/connections (cluster / database / table / status /
 *      last harvest). Neither touches Kusto, so this paints fast.
 *   2. rows — GET v2/eventhouse/documents/{id}/rows, which reads the tenant's
 *      Eventhouse live. Kept OFF the metadata path so a slow or unavailable
 *      Kusto never blocks the header, and so a rows failure degrades to
 *      "metadata + an error line" rather than an empty panel.
 *
 * Paging is a keyset cursor (`next_cursor`), not an offset — Kusto has no
 * OFFSET. "Load more" appends the next page.
 */

/** Rows per request. A preview, not an export — see the endpoint's 500 cap. */
const PAGE_SIZE = 100

type LiveStreamData = {
  description?: string | null
  connection?: EventhouseConnectionSummary
  /** The connections read failed for a reason OTHER than the feature being
   * off — worded differently from "no connection" so a linked stream is never
   * reported as unavailable because of a transient 500. */
  connectionsFailed?: boolean
}

/** Compact UTC rendering: the rows are machine timestamps, so keep them exact
 * and aligned rather than locale-formatted per row. */
const formatRowTime = (iso: string): string => {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toISOString().replace('T', ' ').replace('.000Z', 'Z')
}

const LiveStreamPreview = ({ docId }: { docId: string }) => {
  const { t } = useTranslation()
  const [data, setData] = useState<LiveStreamData | null>(null)
  const [loading, setLoading] = useState(true)

  const [rows, setRows] = useState<EventhouseReadingRow[]>([])
  // From the rows endpoint — structured, unlike the description prose.
  const [tagIds, setTagIds] = useState<string[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(false)
  const [rowsLoading, setRowsLoading] = useState(true)
  // First-page failure: there is nothing to show, so the table is replaced.
  const [rowsError, setRowsError] = useState(false)
  // Load-more failure: kept separate so the rows already on screen survive.
  const [pageError, setPageError] = useState(false)
  // Mirrors docId for the async loadMore to compare against — reading state
  // inside a settled promise would see the value captured at call time.
  const docIdRef = useRef(docId)
  useEffect(() => {
    docIdRef.current = docId
  }, [docId])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      // Clear the spinner before bailing out — returning with `loading` still
      // true leaves the panel spinning forever for an absent docId.
      if (!docId) {
        setLoading(false)
        return
      }
      setLoading(true)
      try {
        // Both are independent; a failing connections probe must still render
        // the document's own description.
        const [doc, connections] = await Promise.all([
          apiService.getDocument(docId).catch(() => null),
          // 403 = the tenant does not have the feature, which is a definite
          // "no connection details for you". Anything else (500, network,
          // auth) is NOT that, and collapsing it to [] would state
          // "unavailable" about a stream that is actually linked — so the two
          // are tracked separately and worded differently below.
          apiService.listEventhouseConnections().catch((error: unknown) => {
            const status = (error as { response?: { status?: number } })?.response?.status
            return { failed: status !== 403 }
          }),
        ])
        if (cancelled) return
        const description = (doc as { description?: string | null } | null)?.description ?? null
        const connectionsFailed = !Array.isArray(connections) && connections.failed
        const connection = Array.isArray(connections)
          ? connections.find((c) => c.documentId === docId)
          : undefined
        setData({ description, connection, connectionsFailed })
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [docId])

  // First page of rows, in its own pass so Kusto latency never delays the
  // header above.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      if (!docId) {
        setRowsLoading(false)
        return
      }
      setRowsLoading(true)
      setRowsError(false)
      setPageError(false)
      setRows([])
      setTagIds([])
      setCursor(null)
      setHasMore(false)
      try {
        const page = await apiService.getEventhouseRows(docId, PAGE_SIZE)
        if (cancelled) return
        setRows(page.rows ?? [])
        setTagIds(page.tag_ids ?? [])
        setCursor(page.next_cursor ?? null)
        setHasMore(Boolean(page.has_more))
      } catch {
        if (!cancelled) setRowsError(true)
      } finally {
        if (!cancelled) setRowsLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [docId])

  const loadMore = useCallback(async () => {
    if (!cursor || rowsLoading) return
    setRowsLoading(true)
    setPageError(false)
    // The doc this page was requested FOR. If the viewer switches documents
    // while the request is in flight, its rows and cursor belong to the old
    // document and must be dropped — appending them would mix two documents'
    // readings and install the wrong cursor.
    const requestedFor = docId
    try {
      const page = await apiService.getEventhouseRows(docId, PAGE_SIZE, cursor)
      if (requestedFor !== docIdRef.current) return
      setRows((previous) => [...previous, ...(page.rows ?? [])])
      setCursor(page.next_cursor ?? null)
      setHasMore(Boolean(page.has_more))
    } catch {
      // A failed NEXT page must not hide the rows already on screen, which is
      // what reusing `rowsError` did — that state replaces the whole table.
      if (requestedFor === docIdRef.current) setPageError(true)
    } finally {
      if (requestedFor === docIdRef.current) setRowsLoading(false)
    }
  }, [cursor, docId, rowsLoading])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400 dark:text-gray-500" />
      </div>
    )
  }

  const conn = data?.connection
  // Prefer the harvest's prose, which carries per-tag DESCRIPTIONS the rows
  // endpoint doesn't return. Fall back to the endpoint's structured `tag_ids`
  // so a reworded description degrades to bare ids instead of showing no tags
  // at all — the silent-empty failure mode review #28036 named.
  const parsedTags = parseTagInventory(data?.description)
  const tags = parsedTags.length > 0 ? parsedTags : tagIds
  const isLive = conn?.status === 'linked'

  return (
    <div className="h-full overflow-auto p-4 flex flex-col gap-4 text-sm">
      <div className="flex items-center gap-2">
        <Radio
          className={`w-4 h-4 ${isLive ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}
        />
        <span className="font-medium">
          {t('document_viewer:live_stream.title', { defaultValue: 'Live connection' })}
        </span>
        {conn && (
          <span
            className={`rounded-md px-2 py-0.5 text-xs font-medium ${
              isLive
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
            }`}
          >
            {conn.status}
          </span>
        )}
      </div>

      {conn ? (
        <div className="grid gap-2">
          <div className="flex items-start gap-2">
            <Database className="w-4 h-4 mt-0.5 shrink-0 text-gray-500 dark:text-gray-400" />
            <div className="min-w-0">
              <div className="font-mono break-all">
                {conn.database} / {conn.tableName}
              </div>
              <div className="text-xs text-gray-500 dark:text-dark-text-muted break-all">
                {conn.clusterUri}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 shrink-0 text-gray-500 dark:text-gray-400" />
            <span className="text-gray-600 dark:text-dark-text-muted">
              {t('document_viewer:live_stream.last_refreshed', { defaultValue: 'Inventory refreshed' })}
              {': '}
              {conn.lastHarvestAt ? new Date(conn.lastHarvestAt).toLocaleString() : '—'}
            </span>
          </div>
          {conn.statusDetail && (
            <div className="text-xs text-red-600 dark:text-red-400 break-words">{conn.statusDetail}</div>
          )}
        </div>
      ) : data?.connectionsFailed ? (
        <div className="text-amber-700 dark:text-amber-400">
          {t('document_viewer:live_stream.connection_error', {
            defaultValue: 'Could not load connection details right now.',
          })}
        </div>
      ) : (
        <div className="text-gray-500 dark:text-dark-text-muted">
          {t('document_viewer:live_stream.no_connection', {
            defaultValue: 'Connection details are unavailable.',
          })}
        </div>
      )}

      {tags.length > 0 && (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-gray-600 dark:text-dark-text-muted">
            <TagIcon className="w-4 h-4 shrink-0" />
            <span>
              {t('document_viewer:live_stream.tags', {
                count: tags.length,
                defaultValue: 'Tags ({{count}})',
              })}
            </span>
          </div>
          <ul className="flex flex-col gap-1 pl-6">
            {tags.map((tag) => (
              <li key={tag} className="font-mono text-xs break-all text-gray-800 dark:text-dark-text">
                {tag}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ---------------------------------------------------------- rows */}
      <div className="flex flex-col gap-2 min-h-0">
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">
            {t('document_viewer:live_stream.rows_title', { defaultValue: 'Readings' })}
          </span>
          {rows.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-dark-text-muted">
              {t('document_viewer:live_stream.rows_shown', {
                count: rows.length,
                // _one/_other live in the locale files; i18next selects on
                // `count`. A single defaultValue here would render
                // "1 rows loaded" whenever the key is missing.
                defaultValue_one: '{{count}} row loaded',
                defaultValue_other: '{{count}} rows loaded',
              })}
            </span>
          )}
        </div>

        {rowsError ? (
          <div className="flex items-start gap-2 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              {t('document_viewer:live_stream.rows_error', {
                defaultValue: 'Could not read rows from Eventhouse right now.',
              })}
            </span>
          </div>
        ) : rows.length === 0 && rowsLoading ? (
          <div className="flex items-center gap-2 text-gray-500 dark:text-dark-text-muted">
            <Loader2 className="w-4 h-4 animate-spin shrink-0" />
            <span>
              {t('document_viewer:live_stream.rows_loading', {
                defaultValue: 'Reading rows from Eventhouse…',
              })}
            </span>
          </div>
        ) : rows.length === 0 ? (
          <div className="text-gray-500 dark:text-dark-text-muted">
            {t('document_viewer:live_stream.rows_empty', {
              defaultValue: 'This table has no rows yet.',
            })}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto border rounded-md dark:border-dark-border">
              <table className="w-full text-xs font-mono border-collapse">
                <thead className="sticky top-0 bg-gray-50 dark:bg-dark-surface">
                  <tr className="text-left text-gray-600 dark:text-dark-text-muted">
                    <th className="px-2 py-1.5 font-medium whitespace-nowrap">
                      {t('document_viewer:live_stream.col_time', { defaultValue: 'Time (UTC)' })}
                    </th>
                    <th className="px-2 py-1.5 font-medium whitespace-nowrap">
                      {t('document_viewer:live_stream.col_tag', { defaultValue: 'Tag' })}
                    </th>
                    <th className="px-2 py-1.5 font-medium text-right whitespace-nowrap">
                      {t('document_viewer:live_stream.col_value', { defaultValue: 'Value' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    // time+tag is the table's sort key but NOT guaranteed
                    // unique by the source, so the index keeps keys stable.
                    <tr
                      key={`${row.time}-${row.tag_id}-${index}`}
                      className="border-t dark:border-dark-border"
                    >
                      <td className="px-2 py-1 whitespace-nowrap text-gray-600 dark:text-dark-text-muted">
                        {formatRowTime(row.time)}
                      </td>
                      <td className="px-2 py-1 whitespace-nowrap">{row.tag_id}</td>
                      <td className="px-2 py-1 text-right whitespace-nowrap">
                        {row.value ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pageError && (
              // Below the table, never in place of it: the rows already loaded
              // stay visible when the NEXT page fails.
              <div className="flex items-start gap-2 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  {t('document_viewer:live_stream.page_error', {
                    defaultValue: 'Could not load more rows. The rows above are unaffected.',
                  })}
                </span>
              </div>
            )}
            {hasMore && (
              <button
                type="button"
                onClick={() => void loadMore()}
                disabled={rowsLoading}
                className="self-start inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium
                           hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed
                           dark:border-dark-border dark:hover:bg-dark-surface"
              >
                {rowsLoading && <Loader2 className="w-3 h-3 animate-spin" />}
                {t(
                  pageError
                    ? 'document_viewer:live_stream.retry'
                    : 'document_viewer:live_stream.load_more',
                  { defaultValue: pageError ? 'Retry' : 'Load more' }
                )}
              </button>
            )}
          </>
        )}
      </div>

      <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-auto">
        {t('document_viewer:live_stream.values_note', {
          defaultValue:
            'Values are not stored here — they are read live from Eventhouse when you ask a question.',
        })}
      </p>
    </div>
  )
}

export default LiveStreamPreview
